import { type JSONContent } from "@vericus/cadmus-editor-prosemirror";

import { Dexie, type Table } from "dexie";

/** Serialised JSON object for a ProseMirror Step */
export type StepJSON = Record<string, unknown>;

export interface SendableChangeset {
  /// the Stream that this sendable changeset is intended for
  streamId: string;
  /// the current live tab's unique ID
  clientId: string;
  /// the base Authority confirmed Version
  version: number;
  /// the base Authority confirmed Doc
  doc: JSONContent;
  /// the mirrored unconfirmed steps of the actual CollabState's in-memory
  /// unconfirmed buffer
  steps: StepJSON[];
  /// unix timestamp when this sendable changeset was written to store
  savedAt: number;
}

export interface SendableChangesetStore {
  get(streamId: string, clientId: string): Promise<SendableChangeset | null>;
  put(sendableChangeset: SendableChangeset): Promise<void>;
  delete(streamId: string, clientId: string): Promise<boolean>;
  list(streamId: string): Promise<SendableChangeset[]>;
  /// release the underlying connection; a later call reopens it
  close(): void;
}

//// Dexie (IndexDB)'s implementation of the store ////

type SendableChangesetKey = [streamId: string, clientId: string];

type SendableChangesetStoreSchema = {
  changesets: Table<SendableChangeset, SendableChangesetKey>;
};

type SendableChangeDb = Dexie & SendableChangesetStoreSchema;

const SENDABLE_CHANGE_DB = "sendable-change";

function openSendableChangeDb(): SendableChangeDb {
  const db = new Dexie(SENDABLE_CHANGE_DB) as SendableChangeDb;

  db.version(1).stores({
    changesets: "[streamId+clientId]",
  });

  return db;
}

export class DexieSendableChangesetStore implements SendableChangesetStore {
  constructor(private readonly db: SendableChangeDb = openSendableChangeDb()) {}

  async get(
    streamId: string,
    clientId: string
  ): Promise<SendableChangeset | null> {
    const row = await this.db.changesets.get([streamId, clientId]);
    return row ?? null;
  }

  async put(sendableChangeset: SendableChangeset): Promise<void> {
    await this.db.changesets.put(sendableChangeset);
  }

  async delete(streamId: string, clientId: string): Promise<boolean> {
    const key: SendableChangesetKey = [streamId, clientId];
    return this.db.transaction("rw", this.db.changesets, async () => {
      const existed = (await this.db.changesets.get(key)) !== undefined;
      await this.db.changesets.delete(key);
      return existed;
    });
  }

  async list(streamId: string): Promise<SendableChangeset[]> {
    return this.db.changesets
      .where("[streamId+clientId]")
      .between([streamId, Dexie.minKey], [streamId, Dexie.maxKey])
      .toArray();
  }

  close(): void {
    // Dexie's default close is permanent; keep auto-open so a backup
    // still in flight after teardown reopens instead of throwing.
    this.db.close({ disableAutoOpen: false });
  }
}
